import { GaugeData } from './types';

export const TURBINES_GROUP_PREVIEW_COUNT = 14;
export const UNKNOWN_LOCATION_KEY = 'unknown';
export const UNKNOWN_LOCATION_LABEL = 'Not Assigned';

export type TurbineLocationGroupData = {
  groupKey: string;
  locationId: string | null;
  locationName: string;
  turbines: GaugeData[];
};

/**
 * Returns whether the gauge has a non-empty locationName.
 * @param {GaugeData} gauge - Turbine gauge data.
 * @returns {boolean} True when locationName is present.
 */
export const hasTurbineLocation = (gauge: GaugeData) => {
  return Boolean((gauge.locationName || '').trim());
};

/**
 * Returns the trimmed locationName used for filter and grouping.
 * @param {GaugeData} gauge - Turbine gauge data.
 * @returns {string} Normalized location name, or empty string when missing.
 */
const getNormalizedLocationName = (gauge: GaugeData) => {
  return (gauge.locationName || '').trim();
};

const getLocationGroupKey = (gauge: GaugeData) => {
  const locationName = getNormalizedLocationName(gauge).toLowerCase();

  if (locationName) return locationName;

  return UNKNOWN_LOCATION_KEY;
};

const getLocationDisplayName = (gauge: GaugeData) => {
  const locationName = getNormalizedLocationName(gauge);

  if (locationName) return locationName;

  return UNKNOWN_LOCATION_LABEL;
};

export const filterTurbinesBySearch = (
  gauges: GaugeData[],
  searchQuery: string
): GaugeData[] => {
  if (!searchQuery.trim()) return gauges;

  const query = searchQuery.toLowerCase();
  return gauges.filter(gauge => {
    const turbineId = gauge.id.toLowerCase().includes(query);
    const turbineName = gauge.title.toLowerCase().includes(query);
    const model = gauge.subtitle.toLowerCase().includes(query);
    const location = getLocationDisplayName(gauge)
      .toLowerCase()
      .includes(query);

    return turbineId || turbineName || model || location;
  });
};

export const groupTurbinesByLocation = (
  gauges: GaugeData[]
): TurbineLocationGroupData[] => {
  const groupsMap = new Map<string, TurbineLocationGroupData>();

  gauges.forEach(gauge => {
    const groupKey = getLocationGroupKey(gauge);
    const existingGroup = groupsMap.get(groupKey);

    if (existingGroup) {
      existingGroup.turbines.push(gauge);

      if (!existingGroup.locationId && gauge.locationId) {
        existingGroup.locationId = gauge.locationId;
      }

      return;
    }

    groupsMap.set(groupKey, {
      groupKey,
      locationId: gauge.locationId || null,
      locationName: getLocationDisplayName(gauge),
      turbines: [gauge]
    });
  });

  return Array.from(groupsMap.values()).sort((groupA, groupB) => {
    if (groupA.groupKey === UNKNOWN_LOCATION_KEY) return 1;
    if (groupB.groupKey === UNKNOWN_LOCATION_KEY) return -1;

    return groupA.locationName.localeCompare(groupB.locationName);
  });
};

export const getTurbineGroupDomId = (groupKey: string) => {
  return groupKey.replace(/[^a-zA-Z0-9_-]/g, '');
};
